import { api_configuration } from "../../common";
import DestructorTree from "../DestructorTree";
import { DatastoreProxy } from "../interface/DatastoreProxy";
import NodePathBuilder from "../navigation/NodePathBuilder";
import { CollectionPathBuilder } from "../navigation/createCollectionPathBuilder";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import { createNodeExistanceFilter } from "../state 2 interface/createPropertiesDictionaryFromColumns";
import {
	ui_scalar_default,
	StateChoice,
	node_path_tail,
	descendant_object_path,
	UIContext,
	StringLike,
} from "../state/expressions";
import supportsSelfReference from "../state/supportsSelfReference";
import { registerEntry } from "./registerClientState";
import { getUserNameOrDefault, ignoreCollectionDefaults } from "./ClientState";
import api_cs = api_configuration.types.client_state.alan_api;
import api_cr = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import uuid = require("./uuid");
import update = require("./../widgets/instructions/update");
import { getCurrentDate, getCurrentDateAndTime } from "../calendar/current";
import messages = require("../front/messages");
import SubscriptionBuilder from "../state 2 interface/SubscriptionBuilder";
import reQuery = require("../state 2 interface/reQuery");
import * as Sets from "../Sets";
import * as Collection from "../state/Collection";
import * as Entry from "../state/Entry";
import * as Text from "../state/Text";
import { iReference } from "../state/Reference";
import { ObservableSet } from "../observableTypes";
import { isSaving } from "../state/Node";
import { getGuiText, getGuiNumber, getGuiStategroup, getGuiCollection } from "../guiModel";
import { TaskManager } from "../TaskManager";
import {
	lambda,
	scalarValue,
	ScalarProvider,
	createExpressionBuilder,
	WeakCache,
	createVariable,
	None,
	Scalar,
	MutableVariable,
	DefaultExpressionBuilder,
} from "../../../../../stream";
import { toStream, toVariable } from "../toStream";
import { Pending, Unknown, Failed, Result, isResolved } from "../Status";
import { iText } from "../state/Text";
import { iStategroup, iState } from "../state/Stategroup";
import { iCollection } from "../state/Collection";
import { definesGraphInsertionPosition } from "../state/Graph";
import clientWorkIsDone from "../state/tasks";

const stringUIScalarDefault = WeakCache<
	api_app.Cui_scalar_default,
	DefaultExpressionBuilder<UIContext, Result<StringLike>>
>((definition) => ui_scalar_default(definition));

const numberUIScalarDefault = WeakCache<
	api_app.Cui_scalar_default,
	DefaultExpressionBuilder<UIContext, Result<number>>
>((definition) => ui_scalar_default(definition));

const stategroupUIScalarDefault = WeakCache<
	api_app.Cui_scalar_default,
	DefaultExpressionBuilder<UIContext, Result<StateChoice>>
>((definition) => ui_scalar_default(definition));

const textDefaultExpression = WeakCache<
	api_app.Ctext__type__property,
	DefaultExpressionBuilder<api_cs.Ctext__type__property, Result<StringLike>>
>((definition): DefaultExpressionBuilder<api_cs.Ctext__type__property, Result<StringLike>> => {
	const gui_text = getGuiText(definition);
	if (!gui_text) return lambda<api_cs.Ctext__type__property, Result<StringLike>>(() => Unknown);
	const has_default = gui_text.properties.default_.state;
	switch (has_default.name) {
		case "no":
			return lambda<api_cs.Ctext__type__property, Result<StringLike>>(() => Unknown);
		case "yes": {
			const $source = has_default.node.properties.source.state;
			switch ($source.name) {
				case "current user":
					return createExpressionBuilder<api_cs.Ctext__type__property>().pipe(() => getUserNameOrDefault() ?? Unknown);
				case "guid":
					return lambda<api_cs.Ctext__type__property, Result<StringLike>>(() => ({ toString: () => uuid.generate() }));
				case "auto select only candidate":
					switch (definition.properties.has_reference.state.name) {
						case "no":
							return lambda<api_cs.Ctext__type__property, Result<StringLike>>(() => Unknown);
						case "yes":
							return lambda<api_cs.Ctext__type__property, Result<string>>((text) =>
								autofillOnlyCandidate(text.properties.navigable.cast("yes"))
							);
					}
					break;
				case "expression":
					return createExpressionBuilder<api_cs.Ctext__type__property>().pipe((text) =>
						stringUIScalarDefault($source.node.inferences.text_with_default().properties.default_)([
							text.component_root,
							null,
							text.parent,
						])
					);
				case "select source":
					return lambda<api_cs.Ctext__type__property, Result<StringLike>>(() => Unknown);
			}
		}
	}
});

const numberDefaultExpression = WeakCache<
	api_app.Cnumber__type__property,
	DefaultExpressionBuilder<api_cs.Cnumber__type__property, Result<number>>
>((definition) => {
	const gui_number = getGuiNumber(definition);
	if (!gui_number) return lambda<api_cs.Cnumber__type__property, Result<number>>(() => Unknown);
	const has_default = gui_number.properties.default_.state;
	switch (has_default.name) {
		case "no":
			return lambda<api_cs.Cnumber__type__property, Result<number>>(() => Unknown);
		case "yes": {
			const $value = has_default.node.properties.value.state;
			switch ($value.name) {
				case "today":
					return lambda<api_cs.Cnumber__type__property, Result<number>>(() => getCurrentDate());
				case "now":
					return lambda<api_cs.Cnumber__type__property, Result<number>>(() => getCurrentDateAndTime());
				case "zero":
					return lambda<api_cs.Cnumber__type__property, Result<number>>(() => 0);
				case "one":
					return lambda<api_cs.Cnumber__type__property, Result<number>>(() => 1);
				case "expression":
					return lambda<api_cs.Cnumber__type__property, Result<number>>((number) =>
						numberUIScalarDefault($value.node.inferences.model_expression().properties.default_)([
							number.component_root,
							null,
							number.parent,
						])
					);
			}
		}
	}
});

const stategroupDefaultExpression = WeakCache<
	api_app.Cstate_group,
	DefaultExpressionBuilder<api_cs.Cstate_group__type__property, Result<StateChoice>>
>((definition) => {
	const gui_stategroup = getGuiStategroup(definition);
	if (!gui_stategroup) return lambda<api_cs.Cstate_group__type__property, Result<StateChoice>>(() => Unknown);
	const has_default = gui_stategroup.properties.default_.state;
	switch (has_default.name) {
		case "no":
			return lambda<api_cs.Cstate_group__type__property, Result<StateChoice>>(() => Unknown);
		case "yes": {
			const $source = has_default.node.properties.source.state;
			switch ($source.name) {
				case "state":
					return lambda<api_cs.Cstate_group__type__property, Result<StateChoice>>(
						() => $source.node.properties.state.ref.key.ref
					);
				case "state switch":
					return lambda<api_cs.Cstate_group__type__property, Result<StateChoice>>((stategroup) =>
						stategroupUIScalarDefault($source.node.inferences.model_state_switch().properties.default_)([
							stategroup.component_root,
							null,
							stategroup.parent,
						])
					);
			}
		}
	}
});

const textDefault = WeakCache<api_cs.Ctext__type__property, Scalar<Result<StringLike>>>((text) =>
	textDefaultExpression(text.inferences.text_type())(text)
);

const numberDefault = WeakCache<api_cs.Cnumber__type__property, Scalar<Result<number>>>((number) =>
	numberDefaultExpression(number.inferences.number_type())(number)
);

const stategroupDefault = WeakCache<api_cs.Cstate_group__type__property, Scalar<Result<StateChoice>>>((stategroup) =>
	stategroupDefaultExpression(stategroup.inferences.state_group_type())(stategroup)
);

type LiveUpdatedObject = {
	live_update: boolean;
};

function isLiveUpdated(live_updated_object: LiveUpdatedObject, destructor: DestructorTree): boolean {
	if (live_updated_object.live_update) {
		return true;
	} else {
		live_updated_object.live_update = true;
		destructor.onDestroy(() => {
			live_updated_object.live_update = false;
		});
		return false;
	}
}

export function cancelDefault(property: api_cs.Cproperty, destructor: DestructorTree): void {
	isLiveUpdated(property as unknown as LiveUpdatedObject, destructor);
}

const defaultsManager = new TaskManager(0);

export const applyingDefaults = new ObservableSet<api_cs.Cproperty>();
export async function onFinishedApplyingDefaults(): Promise<void> {
	await defaultsManager.finished();
	return applyingDefaults.size.await((value) => value <= 0);
}

function startApplying(property: api_cs.Cproperty, destructor: DestructorTree) {
	if (!applyingDefaults.has(property)) {
		destructor.onDestroy(() => stopApplying(property));
		applyingDefaults.add(property);
	}
}

function stopApplying(property: api_cs.Cproperty) {
	applyingDefaults.delete(property);
}

export function applyDefaultsWithUpdates(
	cs_node: api_cs.Cnode,
	destructor: DestructorTree = new DestructorTree()
): void {
	destructor.subscribeToStategroup(cs_node.properties.location, (destructor) => {
		switch (cs_node.properties.location.state.name) {
			case "remote":
				break;
			case "local":
				destructor.subscribeToCollection(
					cs_node.properties.attributes,
					{
						on_add: (attribute, destructor) => {
							attribute.properties.type.switch<void>({
								operation: undefined,
								property: (property) => {
									defaultsManager.queue(() => {
										applyPropertyDefault(property, destructor);
									});
								},
							});
						},
					},
					/*init=*/ true
				);
				break;
			default:
				unexpectedState(cs_node.properties.location.state);
		}
	});
	destructor.subscribeToCollection(
		cs_node.properties.attributes,
		{
			on_add: (attribute, destructor) =>
				attribute.properties.type.switch({
					operation: undefined,
					property: (property) =>
						property.properties.type.switch({
							number: undefined,
							text: undefined,
							file: undefined,
							group: (group) => applyDefaultsWithUpdates(group.properties.node, destructor),
							"state group": (stategroup) =>
								destructor.subscribeToStategroup(stategroup.properties.is_set, (destructor) => {
									stategroup.properties.is_set.switch({
										no: undefined,
										yes: ($) => {
											stategroupPropertySubscribeToSetBy($, destructor);
											applyDefaultsWithUpdates($.properties.state.ref.properties.node, destructor);
										},
									});
								}),
							collection: (collection) =>
								destructor.subscribeToCollection(
									collection.properties.entries,
									{
										on_add: (entry, destructor) => {
											destructor.subscribeToStategroup(entry.properties.location, (destructor) => {
												entry.properties.location.switch({
													remote: undefined,
													local: ($) => {
														propertyEntrySubscribeToSetBy($, destructor);
													},
												});
											});
											applyDefaultsWithUpdates(entry.properties.node, destructor);
										},
									},
									/*init=*/ true
								),
						}),
				}),
		},
		/*init=*/ true
	);
}

export function applyOperationDefaults(
	operation: api_cs.Cyes__started,
	destructor: DestructorTree = new DestructorTree()
): void {
	destructor.subscribeToStategroup(operation.properties.executing, (destructor) => {
		operation.properties.executing.switch({
			yes: undefined,
			no: () => applyDefaultsWithUpdates(operation.properties.node, destructor),
		});
	});
}

function applyPropertyDefault(cs_property: api_cs.Cproperty, destructor: DestructorTree): void {
	if (
		cs_property.destroyed ||
		destructor.destroyed ||
		isLiveUpdated(cs_property as unknown as LiveUpdatedObject, destructor)
	) {
		return;
	}
	const this_node = cs_property.parent.parent;
	switch (cs_property.properties.type.state.name) {
		case "text": {
			const cs_text = cs_property.properties.type.state.node;
			let updating = false;
			const exp = textPropertyModifiedByUser(cs_text).pipe((modified_by_user) => {
				if (modified_by_user) return Unknown;
				return textDefault(cs_text);
			})();
			const subscription = exp.subscribe({
				update: async (value) => {
					if (cs_property.destroyed) return;
					if (isSaving(this_node) || isSetByCollectionDefault(cs_text) || isRemote(cs_property.component_root)) {
						stopApplying(cs_property);
						return;
					}
					if (updating) return;
					switch (value) {
						case Unknown:
							stopApplying(cs_property);
							break;
						case Pending:
							startApplying(cs_property, destructor);
							break;
						case Failed:
							updating = true;
							iText(cs_text).discard();
							stopApplying(cs_property);
							updating = false;
							if (global.alan_debug) {
								(cs_text as any).__alan_default = undefined;
							}
							break;
						default: {
							updating = true;
							const str = value.toString();
							iText(cs_text).update(str, "engine", false);
							stopApplying(cs_property);
							updating = false;
							if (global.alan_debug) {
								(cs_text as any).__alan_default = "applied " + str;
							}
						}
					}
				},
			});
			destructor.onDestroy(() => exp.unsubscribe(subscription));
			break;
		}
		case "number": {
			const cs_number = cs_property.properties.type.state.node;
			if (global.alan_debug) {
				(cs_number as any).__alan_default = "initialized";
			}
			let updating = false;
			const exp = numberPropertyModifiedByUser(cs_number).pipe((modified_by_user) => {
				if (modified_by_user) return Unknown;
				return numberDefault(cs_number);
			})();
			const subscription = exp.subscribe({
				update: (value) => {
					if (cs_property.destroyed) return;
					if (isSaving(this_node) || isRemote(cs_property.component_root)) {
						stopApplying(cs_property);
						return;
					}
					if (updating) return;
					if (global.alan_debug) {
						(cs_number as any).__alan_default = `applied ${String(value)}`;
					}

					switch (value) {
						case Unknown:
							stopApplying(cs_property);
							break;
						case Pending:
							startApplying(cs_property, destructor);
							break;
						case Failed:
							stopApplying(cs_property);
							updating = true;
							update.unset_number(cs_number);
							updating = false;
							break;
						default:
							stopApplying(cs_property);
							updating = true;
							update.number(cs_number, value, "engine");
							updating = false;
					}
				},
			});
			destructor.onDestroy(() => exp.unsubscribe(subscription));
			break;
		}
		case "file":
			break;
		case "group":
			break;
		case "state group": {
			const cs_stategroup = cs_property.properties.type.state.node;
			if (definesGraphInsertionPosition(cs_stategroup)) return;
			let updating = false;
			const exp = stategroupPropertyModifiedByUser(cs_stategroup).pipe((modified_by_user) => {
				if (modified_by_user) return Unknown;
				return stategroupDefault(cs_stategroup);
			})();
			const subscription = exp.subscribe({
				update: (value) => {
					if (cs_property.destroyed) return;
					if (isSaving(this_node) || isRemote(cs_property.component_root)) {
						stopApplying(cs_property);
						return;
					}
					if (updating) return;
					if (global.alan_debug) {
						(cs_stategroup as any).__alan_default = "initialized";
					}

					switch (value) {
						case Unknown:
							stopApplying(cs_property);
							break;
						case Pending:
							startApplying(cs_property, destructor);
							break;
						case Failed:
							stopApplying(cs_property);
							updating = true;
							iStategroup(cs_stategroup).discard();
							updating = false;
							break;
						default: {
							stopApplying(cs_property);
							updating = true;
							iState(cs_stategroup.properties.states.get(value.key)!).__select("engine");
							updating = false;
						}
					}
				},
			});
			destructor.onDestroy(() => exp.unsubscribe(subscription));
			break;
		}
		case "collection": {
			const cs_collection = cs_property.properties.type.state.node;
			const gui_collection = getGuiCollection(cs_collection.inferences.collection_type());
			gui_collection?.properties.default_.switch({
				no: undefined,
				yes: ($) => {
					const subscription_on_ignore = ignoreCollectionDefaults.subscribe({
						update: (ignore) => {
							if (ignore === true || isRemote(cs_property.component_root)) {
								stopApplying(cs_property);
								clearCollectionDefaults(cs_collection);
								return;
							}
							if (global.alan_debug) {
								(cs_collection as any).__alan_default = "initialized";
							}
							const has_default = $.inferences.model_expression();
							const source_collection_def = has_default.properties.key_reference.output
								.definition()
								.output.collection();
							function on_value(source_keys: Set<string>) {
								stopApplying(cs_property);
								if (global.alan_debug) {
									(cs_collection as any).__alan_default = "applied";
								}
								const target_keys: Set<string> = new Set();
								cs_collection.properties.entries.forEach((entry) => {
									const target_key = Entry.getKeyValue(entry);
									if (target_key !== undefined) {
										target_keys.add(target_key);
										entry.properties.location.switch({
											remote: undefined,
											local: ($) => {
												$.properties.modified_by.switch({
													user: undefined,
													engine: () => {
														const is_duplicate = entry.properties.key.inferences.key().properties.key_unique.switch({
															yes: false,
															no: true,
														});
														if (!source_keys.has(target_key) || is_duplicate) {
															iCollection($.parent.parent).removeEntry($.parent);
														}
													},
												});
											},
										});
									}
								});
								source_keys.forEach((source_key) => {
									if (!target_keys.has(source_key)) {
										const entry = iCollection(cs_collection).__addEntry("engine", source_key);
										// update graph only if no default on the edge reference
										for (const graph of iCollection(cs_collection).graphs) {
											graph.edge_reference.properties.ui.properties.default_value.switch({
												no: () => {
													graph.addEntry(entry, "engine");
												},
												yes: () => undefined,
											});
										}
										registerEntry(entry, destructor);
										// the key that is referenced by default does not have to be the collection key (but can be a derived value)
										const target_text_property = has_default.properties.key_reference.output.property().parent;
										const source_key_property = source_collection_def.properties.key_property.ref;
										if (source_key_property !== target_text_property) {
											const cs_target_text_property = entry.properties.node.properties.attributes
												.get(target_text_property.parent.parent.key)!
												.properties.type.cast("property")
												.properties.type.cast("text");
											if (cs_target_text_property !== entry.properties.key.ref) {
												cs_target_text_property.properties.is_set.set("yes", {
													text: source_key,
													by: ["engine", {}],
												});
												cs_target_text_property.properties.backend_known.set("yes", {
													value: source_key,
												});
											}
										}
									}
								});
								// update queries
								reQuery.queriesForCollectionFromNode(cs_property.parent.parent, cs_collection);
								clientWorkIsDone().then(() => {
									if (cs_collection.destroyed) return;
									for (const graph of iCollection(cs_collection).graphs) {
										graph.reset(false);
									}
								});
							}
							const definition = has_default.properties.key_reference.output.definition().variant;
							switch (definition.name) {
								case "reference": {
									const key_reference_definition = definition.definition;
									const exp = collectionPropertyModifiedByUser(cs_collection)
										.pipe((modified_by_user) => {
											if (modified_by_user) {
												return Unknown;
											}
											startApplying(cs_property, destructor);
											return Collection.resolveMatrixSourceCollection(cs_collection, key_reference_definition)();
										})
										.pipe((source_collection) => {
											switch (source_collection) {
												case Unknown:
													stopApplying(cs_property);
													return Unknown;
												case Pending:
													startApplying(cs_property, destructor);
													return Pending;
												case Failed:
													clearCollectionDefaults(cs_collection);
													stopApplying(cs_property);
													return Failed;
												default:
													return subscribeToSourceCollection(
														source_collection,
														key_reference_definition,
														has_default.properties.entry_filter
													);
											}
										})();
									const subscription = exp.subscribe({
										update: (source_collection) => {
											if (cs_property.destroyed) return;
											// huh? this is weird code
											stopApplying(cs_property);
											switch (source_collection) {
												case Unknown:
													stopApplying(cs_property);
													return;
												case Pending:
													startApplying(cs_property, destructor);
													return;
												case Failed:
													clearCollectionDefaults(cs_collection);
													stopApplying(cs_property);
													return;
												default:
													on_value(source_collection);
											}
										},
									});
									destructor.onDestroy(() => exp.unsubscribe(subscription));
									break;
								}
								case "containment":
									//TODO properly implement containment
									stopApplying(cs_property);
									break;
							}
						},
					});
					cs_collection.onDestroy(() => ignoreCollectionDefaults.unsubscribe(subscription_on_ignore));
				},
			});
			break;
		}
	}
}

function isRemote(node: api_cs.Cnode) {
	return node.properties.location.switch({
		local: false,
		remote: true,
	});
}

function clearCollectionDefaults(cs_collection: api_cs.Ccollection) {
	if (cs_collection.destroyed) return;
	cs_collection.properties.entries.forEach((entry) => {
		entry.properties.location.switch({
			remote: undefined,
			local: ($) => {
				$.properties.modified_by.switch({
					user: undefined,
					engine: () => {
						iCollection($.parent.parent).removeEntry($.parent);
					},
				});
			},
		});
	});
}

function subscribeToSourceCollection(
	source_collection: api_cs.Ccollection,
	key_reference_definition: api_app.Centry_reference_definition,
	entry_filter: api_app.Cnode_path_tail
): ScalarProvider<Result<Set<string>>> {
	const source_collection_name = source_collection.parent.parent.key.entry;

	async function getRemoteEntries(id_path_builder: IdPathBuilder, stream: MutableVariable<Result<Set<string>>>) {
		const filters: Record<string, api_cr.Tvalue_filters> = {};
		const node_path = mapObjectPathTailToNodePath(
			key_reference_definition.properties.tail.properties.path,
			new NodePathBuilder()
		);
		if (node_path !== undefined) {
			filters["reference filter"] = createNodePathTailFilter(entry_filter, node_path);
		}

		async function querySourceCollection(): Promise<Result<Set<string>>> {
			const result = await DatastoreProxy.instance.collectionQuery({
				filters: {
					"todo filter": ["no", {}],
					"value filters": filters,
				},
				"context node path": id_path_builder.build(),
				"collection path": CollectionPathBuilder.create().collection(source_collection_name).build(),
				"select entries": [
					"yes",
					{
						"limit number of entries": Number.MAX_SAFE_INTEGER,
						properties: {
							properties: {},
						},
					},
				],
				aggregates: {},
			});
			if (stream.destroyed) {
				return Failed;
			}

			const $context = result.properties.context.state;
			switch ($context.name) {
				case "exists": {
					const $entries = $context.node.properties.entries.state;
					switch ($entries.name) {
						case "no":
							return Unknown;
						case "yes":
							switch ($entries.node.properties.limit_exceeded.state.name) {
								case "yes":
									messages.addMessage("warning", "Not all subcollections copied", {
										body: {
											phrase: "{0} have not been copied because it contains too many items.",
											vars: [source_collection_name],
										},
									});
									return Unknown;
								case "no": {
									const $state = $entries.node.properties.entries.properties.has_steps.state;
									switch ($state.name) {
										case "no":
											return Unknown;
										case "yes":
											return $state.node.properties.entries
												.toArray()
												.reduce<Set<string>>((r, e) => (r.add(e[0]), r), new Set());
									}
								}
							}
							break;
					}
					break;
				}
				case "does not exist":
					return Unknown;
				case "permission denied":
					return Unknown;
			}
		}
		const subscription_builder = SubscriptionBuilder.create();
		const entry_subscription_builder = subscription_builder.collection_entries(source_collection_name);
		mapDescendantObjectPathToSubscriptionBuilder(key_reference_definition.properties.tail, entry_subscription_builder);
		mapNodePathTailToSubscriptionBuilder(entry_filter, entry_subscription_builder);

		const datastore_subscription = DatastoreProxy.instance.subscribe(
			{
				"context node": id_path_builder.build(),
				"mutation permissions": "ignore",
				"node subscription": {
					attributes: subscription_builder.properties,
				},
			},
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			() => {},
			async (notification) => {
				if (stream.destroyed) {
					return;
				}
				switch (notification.properties.context.state.name) {
					case "exists": {
						const entries = await querySourceCollection();
						stream.set(entries);
						break;
					}
					case "permission denied":
						break;
					case "does not exist":
						break;
				}
			}
		);
		stream.onDestroy(() => {
			datastore_subscription.unsubscribe();
		});
		const entries = await querySourceCollection();
		stream.set(entries);
	}

	const source_collection_node = source_collection.parent.parent.parent;
	switch (source_collection_node.properties.location.state.name) {
		case "local": {
			const keyCollection = createVariable(new Set<string>(), true);
			const keyCollector = toStream(source_collection.properties.entries)
				.map(([, entry]) =>
					node_path_tail(entry_filter).pipe((node) => (isResolved(node) ? entry : None))(entry.properties.node)
				)
				.filter()
				.map(([, entry]) =>
					createExpressionBuilder(entry).pipe((entry) =>
						createExpressionBuilder(Text.value(entry.properties.key.ref)).pipe((value) =>
							value === Failed ? None : value
						)()
					)()
				)
				.filter()();
			const subscription = keyCollector.subscribe({
				add: () => {
					keyCollection.set(new Set([...keyCollector.entries.values()].map((v) => scalarValue(v))));
				},
				remove: () => {
					keyCollection.set(new Set([...keyCollector.entries.values()].map((v) => scalarValue(v))));
				},
			});
			source_collection.onDestroy(() => {
				keyCollection.destroy();
				keyCollector.unsubscribe(subscription);
			});
			return keyCollection;
		}
		case "remote": {
			const remote_stream = createVariable<Result<Set<string>>>(Pending, true);
			getRemoteEntries(createIdPathBuilderFromNode(source_collection_node), remote_stream);
			return remote_stream;
		}
		default:
			unexpectedState(source_collection_node.properties.location.state);
	}
}

function mapDescendantObjectPathToQueryFilters(path: api_app.Cdescendant_object_path): api_cr.Tvalue_filters[] {
	const node_path = mapObjectPathTailToNodePath(path.properties.path, new NodePathBuilder());
	if (node_path) {
		return [createNodeExistanceFilter(node_path.build())];
	}
	return [];
}
function mapObjectPathTailToNodePath(
	path: api_app.Cobject_path_tail,
	builder: NodePathBuilder
): NodePathBuilder | undefined {
	return path.properties.has_steps.switch({
		no: () => builder,
		yes: ($) => {
			const tail = $.properties.tail;
			return $.properties.type.switch({
				parent: () => builder.parent(),
				dependency: () => undefined, //TODO implement?
				"reference set": () => undefined,
				"property value": ($) =>
					$.properties.property.properties.property.ref.properties.type.switch({
						number: () => undefined,
						text: () => undefined,
						file: () => undefined,
						collection: () => undefined,
						group: () =>
							mapObjectPathTailToNodePath(tail, builder.group($.properties.property.properties.property.entry)),
						"state group": () => mapObjectPathTailToNodePath(tail, builder),
					}),
				state: ($) => {
					const stategroup = $.properties.state.ref.parent.parent.parent.key;
					const state = $.properties.state.entry;
					return mapObjectPathTailToNodePath(tail, builder.state(stategroup, state));
				},
			});
		},
	});
}

function createNodePathTailFilter(path: api_app.Cnode_path_tail, builder: NodePathBuilder): api_cr.Tvalue_filters {
	return createNodeExistanceFilter(mapNodePathTailToNodePath(path, builder).build());
}
function mapNodePathTailToNodePath(path: api_app.Cnode_path_tail, path_builder: NodePathBuilder): NodePathBuilder {
	return path.properties.has_steps.switch({
		yes: ($has_step) =>
			$has_step.properties.step.properties.type.switch({
				parent: () => mapNodePathTailToNodePath($has_step.properties.tail, path_builder.parent()),
				group: ($) =>
					mapNodePathTailToNodePath($has_step.properties.tail, path_builder.group($.properties.group.entry)),
				"property rule": ($) =>
					mapNodePathTailToNodePath(
						$has_step.properties.tail,
						path_builder.reference_rule($.properties.text.entry, $.properties.rule.entry)
					),
				reference: ($) =>
					mapNodePathTailToNodePath($has_step.properties.tail, path_builder.reference($.properties.text.entry)),
				state: ($) =>
					mapNodePathTailToNodePath(
						$has_step.properties.tail,
						path_builder.state($.properties.state_group.entry, $.properties.state.entry)
					),
				"state context rule": ($) =>
					mapNodePathTailToNodePath($has_step.properties.tail, path_builder.state_rule($.properties.rule.entry)),
			}),
		no: path_builder,
	});
}

function mapDescendantObjectPathToSubscriptionBuilder(
	path: api_app.Cdescendant_object_path,
	subscription_builder: SubscriptionBuilder
): void {
	return mapObjectPathToSubscriptionBuilder(path.properties.path, subscription_builder);
}
function mapObjectPathToSubscriptionBuilder(
	path: api_app.Cobject_path_tail,
	subscription_builder: SubscriptionBuilder
): void {
	switch (path.properties.has_steps.state.name) {
		case "yes": {
			const $ = path.properties.has_steps.state.node;
			const tail = $.properties.tail;
			switch ($.properties.type.state.name) {
				case "parent":
					// Not supported
					break;
				case "dependency":
					// Not supported
					break;
				case "reference set":
					// Not supported
					break;
				case "property value": {
					const property = $.properties.type.state.node.properties.property.properties.property.ref;
					switch (property.properties.type.state.name) {
						case "text":
							break;
						case "number":
							break;
						case "file":
							break;
						case "collection":
							break;
						case "group":
							mapObjectPathToSubscriptionBuilder(tail, subscription_builder.group(property.parent.key));
							break;
						case "state group":
							mapObjectPathToSubscriptionBuilder(tail, subscription_builder);
							break;
						default:
							unexpectedState(property.properties.type.state);
					}
					break;
				}
				case "state": {
					const stategroup = $.properties.type.state.node.properties.state.ref.parent;
					const state_name = $.properties.type.state.node.properties.state.entry;
					mapObjectPathToSubscriptionBuilder(
						tail,
						subscription_builder.state(stategroup.parent.parent.key, state_name, stategroup)
					);
					break;
				}
				default:
					unexpectedState($.properties.type.state);
			}
			break;
		}
		case "no":
			break;
		default:
			unexpectedState(path.properties.has_steps.state);
	}
}

function mapNodePathTailToSubscriptionBuilder(
	path: api_app.Cnode_path_tail,
	subscription_builder: SubscriptionBuilder
): void {
	switch (path.properties.has_steps.state.name) {
		case "yes": {
			const $ = path.properties.has_steps.state.node;
			const tail = $.properties.tail;
			switch ($.properties.step.properties.type.state.name) {
				case "parent":
					// Not supported
					break;
				case "group": {
					const group_name = $.properties.step.properties.type.state.node.properties.group.entry;
					mapNodePathTailToSubscriptionBuilder(tail, subscription_builder.group(group_name));
					break;
				}
				case "property rule":
					// Not supported
					break;
				case "reference":
					// Not supported
					break;
				case "state": {
					const stategroup = $.properties.step.properties.type.state.node.properties.state_group;
					const state_name = $.properties.step.properties.type.state.node.properties.state.entry;
					mapNodePathTailToSubscriptionBuilder(
						tail,
						subscription_builder.state(stategroup.entry, state_name, stategroup.ref)
					);
					break;
				}
				case "state context rule":
					// Not supported
					break;
				default:
					unexpectedState($.properties.step.properties.type.state);
			}
			break;
		}
		case "no":
			break;
		default:
			unexpectedState(path.properties.has_steps.state);
	}
}

function createDormantFilter(collection: api_cs.Ccollection): api_cr.Tvalue_filters[] {
	const ui_collection_attributes = collection.inferences.collection_type().properties.ui;
	const filters: api_cr.Tvalue_filters[] = [];
	switch (ui_collection_attributes.properties.can_be_dormant.state.name) {
		case "yes": {
			const $ = ui_collection_attributes.properties.can_be_dormant.state.node;
			let path_builder = new NodePathBuilder();
			let has_steps = $.properties.expression.properties.has_steps;
			while (has_steps.state.name === "yes") {
				const step = has_steps.state.node.properties.step;
				switch (step.properties.type.state.name) {
					case "state": {
						const $ = step.properties.type.state.node;
						const stategroup_name = $.properties.state_group.entry;
						const state_name = $.properties.state.entry;
						switch (has_steps.state.node.properties.tail.properties.has_steps.state.name) {
							case "no": {
								const states: api_cr.Tstate_group__type__property__type__value_filters["states to include"] = {};
								for (const sn of $.properties.state_group.ref.properties.states.keys()) {
									if (state_name !== sn) {
										states[sn] = {};
									}
								}
								filters.push({
									path: path_builder.build(),
									type: [
										"property",
										{
											property: stategroup_name,
											type: [
												"state group",
												{
													"states to include": states,
												},
											],
										},
									],
								});
								break;
							}
							case "yes":
								path_builder = path_builder.state(stategroup_name, state_name);
								break;
							default:
								unexpectedState(has_steps.state.node.properties.tail.properties.has_steps.state);
						}
						break;
					}
					case "group": {
						const $ = step.properties.type.state.node;
						path_builder = path_builder.group($.properties.group.entry);
						break;
					}
					case "parent":
						throw new Error("parent step not yet supported in dormant filter path");
					case "property rule":
						throw new Error("property rule step not yet supported in dormant filter path");
					case "state context rule":
						throw new Error("state context rule step not yet supported in dormant filter path");
					case "reference":
						throw new Error("reference step not yet supported in dormant filter path");
					default:
						unexpectedState(step.properties.type.state);
				}
				has_steps = has_steps.state.node.properties.tail.properties.has_steps;
			}
			break;
		}
		case "no":
			break;
		default:
			unexpectedState(ui_collection_attributes.properties.can_be_dormant.state);
	}
	return filters;
}

function autofillOnlyCandidate(cs_reference: api_cs.Cyes__navigable): ScalarProvider<Result<string>> {
	const this_node = cs_reference.parent.parent.parent.parent;
	const reference_expression = cs_reference.inferences.is_navigable().properties.expression;
	const self_reference_allowed = supportsSelfReference(reference_expression);

	const stream = createVariable<Result<string>>(Pending, true);
	const on_candidates = (candidates: Set<string>) => {
		if (candidates.size === 1) {
			stream.set([...candidates][0]);
		} else {
			stream.set(Failed);
		}
	};
	const filter = mapDescendantObjectPathToQueryFilters(reference_expression.properties.tail);
	let collection_path = CollectionPathBuilder.create().collection(
		reference_expression.output.collection().parent.parent.key
	);
	let local_candidates = new Set<string>();
	let remote_candidates = new Set<string>();

	let destructor: DestructorTree | undefined;
	const reference_context = iReference(cs_reference).collectionContext;
	const subscription = reference_context.subscribe({
		update: async (cs_collection) => {
			destructor?.destroy();
			if (!isResolved(cs_collection)) {
				return on_candidates(new Set<string>());
			}
			destructor = new DestructorTree();

			const on_change = () => {
				switch (cs_reference.parent.properties.is_key.state.name) {
					case "no":
						return on_candidates(Sets.union(local_candidates, remote_candidates));
					case "yes": {
						const key_property = cs_reference.parent.properties.is_key.state.node;
						const local_entries = getLocalEntries(key_property);
						const candidates = Sets.union(local_candidates, remote_candidates);
						return on_candidates(Sets.difference(candidates, local_entries));
					}
					default:
						unexpectedState(cs_reference.parent.properties.is_key.state);
				}
			};

			const local_candidates_stream = localCandidatesStream(
				cs_collection,
				reference_expression.properties.tail,
				(entry) => self_reference_allowed || entry.properties.node !== this_node.input.entity().output.node()
			);
			const local_candidates_subscription = local_candidates_stream.subscribe({
				update: (candidates) => {
					local_candidates = candidates;
					on_change();
				},
			});
			destructor.onDestroy(() => local_candidates_stream.unsubscribe(local_candidates_subscription));

			switch (cs_collection.parent.parent.parent.properties.location.state.name) {
				case "remote": {
					let context_path = createIdPathBuilderFromNode(cs_collection.parent.parent.parent);
					let max_remote_entries: number;
					switch (cs_reference.parent.properties.is_key.state.name) {
						case "no":
							max_remote_entries = 1;
							break;
						case "yes": {
							const key_property = cs_reference.parent.properties.is_key.state.node;
							const cs_collection = this_node.input.position().cast("entity").cast("entry").parent;
							const local_entries = getLocalEntries(key_property);
							max_remote_entries = local_entries.size + 1;
							const col_attribute = cs_collection.parent.parent;
							switch (col_attribute.parent.properties.location.state.name) {
								case "local":
									break;
								case "remote":
									context_path = createIdPathBuilderFromNode(col_attribute.parent);
									collection_path = CollectionPathBuilder.create().candidates(col_attribute.key.entry);
									break;
								default:
									unexpectedState(col_attribute.parent.properties.location.state);
							}
							break;
						}
						default:
							unexpectedState(cs_reference.parent.properties.is_key.state);
					}
					remote_candidates = await queryRemoteCandidates(
						filter.concat(createDormantFilter(cs_collection)),
						context_path,
						collection_path,
						max_remote_entries
					);
					break;
				}
				case "local":
					break;
			}
			if (destructor.destroyed !== true) {
				on_change();
			}
		},
	});
	cs_reference.onDestroy(() => {
		destructor?.destroy();
		reference_context.unsubscribe(subscription);
	});
	return stream;
}

function getLocalEntries(this_key_property: api_cs.Cyes__is_key): Set<string> {
	const entries = new Set<string>();
	const collection = this_key_property.inferences.entry().parent;
	for (const entry of collection.properties.entries.values()) {
		switch (entry.properties.location.state.name) {
			case "remote":
				break;
			case "local": {
				const that_key_property = entry.properties.key.ref;
				switch (that_key_property.properties.is_set.state.name) {
					case "no":
						break;
					case "yes": {
						const key = that_key_property.properties.is_set.state.node.properties.text;
						if (that_key_property !== this_key_property.parent) {
							entries.add(key);
						}
						break;
					}
					default:
						unexpectedState(that_key_property.properties.is_set.state);
				}
				break;
			}
			default:
				unexpectedState(entry.properties.location.state);
		}
	}
	return entries;
}

function localCandidatesStream(
	cs_collection: api_cs.Ccollection,
	tail_path: api_app.Cdescendant_object_path,
	entry_filter: (entry: api_cs.Centries__collection) => boolean
) {
	const keyCollector = toStream(cs_collection.properties.entries)
		.map(([, entry]) => (entry_filter(entry) ? entry : None))
		.filter()
		.map(([, entry]) =>
			createExpressionBuilder(entry).pipe((entry) =>
				toStream(entry.properties.location).stateSwitch({
					remote: () => None as Scalar<string | None>,
					local: () =>
						toStream(entry.properties.key.ref.properties.is_set).stateSwitch({
							no: () => None as Scalar<string | None>,
							yes: (key_is_set) =>
								descendant_object_path(tail_path).pipe((tail) =>
									isResolved(tail) ? (toVariable(key_is_set, "text") as ScalarProvider<string | None>) : None
								)(entry.properties.node),
						})(),
				})()
			)()
		)
		.filter()();
	const keyCollection = createVariable(new Set<string>(), true);
	const subscription = keyCollector.subscribe({
		add: () => {
			keyCollection.set(new Set([...keyCollector.entries.values()].map((v) => scalarValue(v))));
		},
		remove: () => {
			keyCollection.set(new Set([...keyCollector.entries.values()].map((v) => scalarValue(v))));
		},
	});
	cs_collection.onDestroy(() => {
		keyCollection.destroy();
		keyCollector.unsubscribe(subscription);
	});
	return keyCollection;
}

async function queryRemoteCandidates(
	filter: api_cr.Tvalue_filters[],
	context_path: IdPathBuilder,
	collection_path: CollectionPathBuilder,
	max_candidates: number
) {
	const reply = await DatastoreProxy.instance.collectionQuery({
		filters: {
			"todo filter": ["no", {}],
			"value filters": filter.reduce<Record<string, api_cr.Tvalue_filters>>((obj, property, i) => {
				obj[i.toString()] = property;
				return obj;
			}, {}),
		},
		"context node path": context_path.build(),
		"collection path": collection_path.build(),
		"select entries": [
			"yes",
			{
				"limit number of entries": max_candidates + 1,
				properties: {
					properties: {},
				},
			},
		],
		aggregates: {},
	});
	let candidates = new Set<string>();
	reply.properties.context.switch({
		"does not exist": () => undefined,
		"permission denied": () => undefined,
		exists: ($) =>
			$.properties.entries.switch({
				no: () => undefined,
				yes: ($) =>
					$.properties.entries.properties.has_steps.switch({
						yes: ($) => {
							candidates = new Set($.properties.entries.toArray().map(($) => $[0]));
						},
						no: () => undefined,
					}),
			}),
	});
	return candidates;
}

function textPropertyModifiedByUser(cs_text: api_cs.Ctext__type__property) {
	return toStream(cs_text.properties.backend_known)
		.stateSwitch({
			no: () =>
				toStream(cs_text.properties.is_set).stateSwitch({
					no: () => false,
					yes: (is_set) =>
						toStream(is_set.properties.by).stateSwitch({
							engine: () => false,
							user: () => true,
						})(),
				})(),
			yes: () => true,
		})
		.pipe((value) => value); // This pipe acts as a 'unique' filter
}

function numberPropertyModifiedByUser(cs_number: api_cs.Cnumber__type__property) {
	return toStream(cs_number.properties.backend_known)
		.stateSwitch({
			no: () =>
				toStream(cs_number.properties.is_set).stateSwitch({
					no: () => false,
					yes: (is_set) =>
						toStream(is_set.properties.by).stateSwitch({
							engine: () => false,
							user: () => true,
						})(),
				})(),
			yes: () => true,
		})
		.pipe((value) => value); // This pipe acts as a 'unique' filter
}

function stategroupPropertyModifiedByUser(cs_stategroup: api_cs.Cstate_group__type__property) {
	return toStream(cs_stategroup.properties.initialized)
		.stateSwitch({
			no: () =>
				toStream(cs_stategroup.properties.is_set).stateSwitch({
					no: () => false,
					yes: (is_set) =>
						toStream(is_set.properties.by).stateSwitch({
							engine: () => false,
							user: () => true,
						})(),
				})(),
			yes: () => true,
		})
		.pipe((value) => value); // This pipe acts as a 'unique' filter
}

function collectionPropertyModifiedByUser(cs_collection: api_cs.Ccollection) {
	return toStream(cs_collection.properties.modified)
		.stateSwitch({
			no: () => false,
			yes: (is_set) =>
				toStream(is_set.properties.by).stateSwitch({
					engine: () => false,
					user: () => true,
				})(),
		})
		.pipe((value) => value); // This pipe acts as a 'unique' filter
}

function stategroupPropertySubscribeToSetBy(
	definition: api_cs.Cyes__is_set__state_group__type__property,
	destructor: DestructorTree
) {
	/*
	If any child property of the selected state is set by the user, we should not set any defaults for this stategroup anymore
	and we set the 'by' property for this stategroup to 'user'.
	If no more child properties are set by the user, we switch to 'by':'engine' again.
	Except however when the stategroup itself is already set by the user, in which case we start with a count of 1
	*/
	let count: number = definition.properties.by.switch({
		user: 1,
		engine: 0,
	});
	subscribeToChildPropertiesSetByUser(
		definition.properties.state.ref.properties.node,
		destructor,
		(set_by_user, init) => {
			/*
		Here 'init' is true when the subscription is returned for the first time with the initial value.
		For the initial values, we only want to count the child properties that are set by the user,
		so if init==true we only update the count when it is set by the user,
		and after that we want to know when they have changed, so if init==false we increment and decrement the count.
		This gives the following logic:
		*/
			if (set_by_user) {
				count = count + 1;
			} else if (!init) {
				count = count - 1;
			}
			if (count <= 0) {
				definition.properties.by.ensure("engine", {});
			} else {
				definition.properties.by.ensure("user", {});
			}
		}
	);
}
function propertyEntrySubscribeToSetBy(definition: api_cs.Clocal__location__entries, destructor: DestructorTree) {
	let count: number = definition.properties.modified_by.switch({
		user: 1,
		engine: 0,
	});
	subscribeToChildPropertiesSetByUser(definition.parent.properties.node, destructor, (set_by_user, init) => {
		if (set_by_user) {
			count = count + 1;
		} else if (!init) {
			count = count - 1;
		}
		if (count <= 0) {
			if (definition.properties.modified_by.state.name === "user") {
				// only update if not already in that state
				definition.properties.modified_by.set("engine", {});
			}
		} else {
			if (definition.properties.modified_by.state.name === "engine") {
				// only update if not already in that state
				definition.properties.modified_by.set("user", {});
			}
		}
	});
}
function subscribeToChildPropertiesSetByUser(
	node: api_cs.Cnode,
	destructor: DestructorTree,
	onSetByUser: (set_by_user: boolean, init: boolean) => void
) {
	node.properties.attributes.forEach((attribute) =>
		attribute.properties.type.switch({
			operation: undefined,
			property: (property) =>
				property.properties.type.switch({
					file: undefined,
					collection: undefined,
					group: (group) => subscribeToChildPropertiesSetByUser(group.properties.node, destructor, onSetByUser),
					text: (text) =>
						destructor.subscribeToStategroup(text.properties.is_set, (destructor) => {
							text.properties.is_set.switch({
								no: undefined,
								yes: (yes) =>
									destructor.subscribeToStategroup(yes.properties.by, (_, first) =>
										onSetByUser(
											yes.properties.by.switch({
												user: true,
												engine: false,
											}),
											first
										)
									),
							});
						}),
					number: (number) =>
						destructor.subscribeToStategroup(number.properties.is_set, (destructor) => {
							number.properties.is_set.switch({
								no: undefined,
								yes: (yes) =>
									destructor.subscribeToStategroup(yes.properties.by, (_, first) =>
										onSetByUser(
											yes.properties.by.switch({
												user: true,
												engine: false,
											}),
											first
										)
									),
							});
						}),
					"state group": (stategroup) =>
						destructor.subscribeToStategroup(stategroup.properties.is_set, (destructor) => {
							stategroup.properties.is_set.switch({
								no: undefined,
								yes: (yes) =>
									destructor.subscribeToStategroup(yes.properties.by, (_, first) =>
										onSetByUser(
											yes.properties.by.switch({
												user: true,
												engine: false,
											}),
											first
										)
									),
							}); // we don't have to recurse into the stategroup node, this is already handled
						}),
				}),
		})
	);
}

function isSetByCollectionDefault(text_property: api_cs.Ctext__type__property) {
	switch (text_property.properties.is_key.state.name) {
		case "yes": {
			const $ = text_property.properties.is_key.state.node;
			return $.inferences.entry().properties.location.switch({
				local: ($) =>
					$.properties.modified_by.switch({
						user: false,
						engine: true,
					}),
				remote: false,
			});
		}
		case "no":
			return false;
	}
}
